import { X } from "lucide-react";
import {
  FIELD_CARRIES,
  FIELD_SHOWS,
  GDPR_QUESTIONS,
  QUALITY_QUESTIONS,
  RESUME_OPTIONS,
  SECURITY_OPTIONS,
  TRACKING_OPTIONS,
  UNBRANDED_OPTIONS,
  WHAT_WE_SELL,
  WHAT_WE_USE,
  emptyDeliveryField,
  type AssessmentAnswers,
  type AssessmentRequest,
  type DeliveryField,
  type GdprAnswer,
  type SectionId,
} from "@shared-core/domain/supplier-assessment";
import { Button } from "../../app/components/primitives/button";
import { Input } from "../../app/components/primitives/input";
import { Textarea } from "../../app/components/primitives/textarea";
import { FormField } from "../../app/components/patterns/FormField";
import { Banner } from "../shell/states";
import {
  Attach,
  Choice,
  SubHeading,
  Ticks,
  YES_NO,
} from "./AssessmentControls";

type SetAnswers = (change: Partial<AssessmentAnswers>) => void;

interface SectionProps {
  request: AssessmentRequest;
  answers: AssessmentAnswers;
  set: SetAnswers;
  /** The wording to accept, or the wording accepted. */
  agreement: { version: string; text: string };
}

/** One section of the supplier assessment, as the supplier answers it. */
export function AssessmentSectionBody({
  section,
  ...props
}: SectionProps & { section: SectionId }) {
  switch (section) {
    case "company":
      return <CompanySection {...props} />;
    case "products":
      return <ProductsSection {...props} />;
    case "despatch":
      return <DespatchSection {...props} />;
    case "quality_manager":
      return <QualityManagerSection {...props} />;
    case "iso":
      return <IsoSection {...props} />;
    case "quality_questions":
      return <QualityQuestionsSection {...props} />;
    case "insurance":
      return <InsuranceSection {...props} />;
    case "slavery":
      return <SlaverySection {...props} />;
    case "gdpr":
      return <GdprSection {...props} />;
    case "security":
      return <SecuritySection {...props} />;
    case "continuity":
      return <ContinuitySection {...props} />;
    case "agreement":
      return <AgreementSection {...props} />;
    case "review":
      return null;
  }
}

// The form's inputs are white with a visible border, so they stand out on the card and on a field row's grey (Ben, 3 Nov).
const INPUT_CLASS = "border-gray-300 bg-card shadow-xs dark:border-input";

function Text({
  id,
  label,
  value,
  onChange,
  hint,
  placeholder,
  type = "text",
  isNumber = false,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
  placeholder?: string;
  type?: string;
  /** Digits only, with the phone's number pad. */
  isNumber?: boolean;
}) {
  return (
    <FormField id={id} label={label} hint={hint}>
      {(control) => (
        <Input
          {...control}
          type={type}
          inputMode={isNumber ? "numeric" : undefined}
          className={INPUT_CLASS}
          value={value}
          placeholder={placeholder}
          onChange={(event) =>
            onChange(
              isNumber
                ? event.target.value.replace(/\D/g, "")
                : event.target.value,
            )
          }
        />
      )}
    </FormField>
  );
}

function CompanySection({ request, answers: a, set }: SectionProps) {
  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2">
        <Text
          id="contact-name"
          label="Contact name"
          value={a.contact_name}
          onChange={(v) => set({ contact_name: v })}
        />
        <Text
          id="company-name"
          label="Company name"
          value={a.company_name}
          onChange={(v) => set({ company_name: v })}
        />
        <Text
          id="registration-number"
          label="Company registration number"
          value={a.registration_number}
          onChange={(v) => set({ registration_number: v })}
          hint="We use it for a credit check with Creditsafe."
        />
        {request.outside_uk && (
          <Text
            id="eori-number"
            label="EORI number"
            value={a.eori_number}
            onChange={(v) => set({ eori_number: v })}
            hint="Customs needs it for goods that cross the UK border."
          />
        )}
        <Text
          id="phone"
          label="Telephone"
          type="tel"
          value={a.phone}
          onChange={(v) => set({ phone: v })}
        />
        <Text
          id="email"
          label="Email"
          type="email"
          value={a.email}
          onChange={(v) => set({ email: v })}
        />
        <Text
          id="postcode"
          label="Postcode"
          value={a.postcode}
          onChange={(v) => set({ postcode: v })}
        />
      </div>
      <FormField id="address" label="Address">
        {(control) => (
          <Textarea
            {...control}
            className={INPUT_CLASS}
            rows={3}
            value={a.address}
            onChange={(event) => set({ address: event.target.value })}
          />
        )}
      </FormField>
    </>
  );
}

function ProductsSection({ answers: a, set }: SectionProps) {
  const ticked = a.sells.length + a.uses.length;
  return (
    <>
      <p className="text-sm">
        Tick everything you can provide for us. Open a group to see what is in
        it.
      </p>
      <p className="text-xs text-muted-foreground" aria-live="polite">
        {ticked ? `${ticked} ticked` : "Nothing ticked yet."}
      </p>
      <SubHeading>Work we sell on to our customers</SubHeading>
      <div className="overflow-hidden rounded-lg border bg-card">
        {WHAT_WE_SELL.map(({ group, items }) => {
          const count = items.filter(([code]) => a.sells.includes(code)).length;
          return (
            <details key={group} className="border-b last:border-b-0">
              <summary className="flex min-h-11 cursor-pointer items-center justify-between gap-3 px-3.5 text-sm font-medium">
                {group}
                <span className="text-xs font-normal text-muted-foreground">
                  {count ? `${count} ticked` : `${items.length} to choose from`}
                </span>
              </summary>
              <div className="px-3.5 pb-3">
                <Ticks
                  legend={`${group} you provide`}
                  options={items}
                  chosen={a.sells}
                  onChange={(sells) => set({ sells })}
                  columns
                />
              </div>
            </details>
          );
        })}
      </div>
      <SubHeading>Goods and services we use ourselves</SubHeading>
      <Ticks
        legend="What you could supply to Pollards itself"
        options={WHAT_WE_USE}
        chosen={a.uses}
        onChange={(uses) => set({ uses })}
        columns
      />
      <Text
        id="provides-other"
        label="Anything else you provide (optional)"
        value={a.provides_other}
        onChange={(v) => set({ provides_other: v })}
        hint="Can’t see what you provide? Tell us here and we’ll find the right place for it."
      />
    </>
  );
}

function DespatchSection({ answers: a, set }: SectionProps) {
  const setField = (i: number, change: Partial<DeliveryField>) =>
    set({
      delivery_fields: a.delivery_fields.map((f, j) =>
        j === i ? { ...f, ...change } : f,
      ),
    });
  return (
    <>
      <SubHeading>Getting our customer’s details onto the delivery</SubHeading>
      <p className="text-sm">
        When you deliver to our customers, we usually want these to reach the
        person receiving the goods:
      </p>
      <ul className="list-disc pl-5 text-sm">
        <li>our customer’s own order or PO number</li>
        <li>
          delivery instructions, for example “Goods in, bay 3, before 10am”
        </li>
        <li>a contact name and phone number for the delivery</li>
        <li>
          who the delivery is from: our name, or our customer’s, instead of
          yours
        </li>
      </ul>
      <p className="text-sm">
        Which fields on your order form, website or system can we fill in for
        these? Add one row for each field.
      </p>
      {!a.no_delivery_fields && (
        <>
          {a.delivery_fields.map((f, i) => (
            <div
              key={i}
              className="grid gap-3 rounded-lg border bg-muted/40 p-3"
            >
              <div className="grid gap-3 sm:grid-cols-[1fr_12rem]">
                <Text
                  id={`field-${i}-name`}
                  label="What you call the field"
                  value={f.name}
                  placeholder="For example “Your reference”"
                  onChange={(name) => setField(i, { name })}
                />
                <Text
                  id={`field-${i}-max`}
                  label="Max characters"
                  value={f.max_characters}
                  placeholder="Blank if no limit"
                  isNumber
                  onChange={(max_characters) => setField(i, { max_characters })}
                />
              </div>
              <Ticks
                legend="What it can carry"
                options={FIELD_CARRIES}
                chosen={f.carries}
                onChange={(carries) => setField(i, { carries })}
              />
              {f.carries.includes("other") && (
                <Text
                  id={`field-${i}-other`}
                  label="What else it carries"
                  value={f.other}
                  onChange={(other) => setField(i, { other })}
                />
              )}
              <Ticks
                legend="Where it shows"
                options={FIELD_SHOWS}
                chosen={f.shows}
                onChange={(shows) => setField(i, { shows })}
              />
              {a.delivery_fields.length > 1 && (
                <div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      set({
                        delivery_fields: a.delivery_fields.filter(
                          (_, j) => j !== i,
                        ),
                      })
                    }
                  >
                    <X aria-hidden /> Remove this field
                  </Button>
                </div>
              )}
            </div>
          ))}
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                set({
                  delivery_fields: [...a.delivery_fields, emptyDeliveryField()],
                })
              }
            >
              Add another field
            </Button>
          </div>
        </>
      )}
      <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={a.no_delivery_fields}
          onChange={(event) =>
            set({ no_delivery_fields: event.target.checked })
          }
          className="size-4 accent-primary"
        />
        We have no fields like this
      </label>

      <SubHeading>Leaving your name off</SubHeading>
      <Choice
        name="unbranded"
        legend="Can your labels, delivery notes and packaging carry no name or branding of yours?"
        value={a.unbranded}
        options={UNBRANDED_OPTIONS}
        onChange={(unbranded) => set({ unbranded })}
      />

      <SubHeading>Our paperwork in the parcel</SubHeading>
      <Choice
        name="encloses"
        legend="If we send you a document, such as our own delivery note or our customer’s paperwork, can you put it in with the goods?"
        value={a.encloses_our_paperwork}
        options={YES_NO}
        onChange={(v) => set({ encloses_our_paperwork: v as "yes" | "no" })}
      />

      <SubHeading>Couriers</SubHeading>
      <div className="grid gap-2">
        <span className="text-xs text-muted-foreground">
          Which couriers do you use? Add each one.
        </span>
        {a.couriers.map((c, i) => (
          <div key={i} className="flex gap-2">
            <Input
              aria-label={`Courier ${i + 1}`}
              className={INPUT_CLASS}
              value={c}
              placeholder="For example DPD"
              onChange={(event) =>
                set({
                  couriers: a.couriers.map((x, j) =>
                    j === i ? event.target.value : x,
                  ),
                })
              }
            />
            {a.couriers.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remove courier ${i + 1}`}
                onClick={() =>
                  set({ couriers: a.couriers.filter((_, j) => j !== i) })
                }
              >
                <X />
              </Button>
            )}
          </div>
        ))}
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => set({ couriers: [...a.couriers, ""] })}
          >
            Add another courier
          </Button>
        </div>
      </div>
      <Choice
        name="tracking"
        legend="Do you give a tracking number for each parcel?"
        value={a.tracking}
        options={TRACKING_OPTIONS}
        onChange={(tracking) => set({ tracking })}
      />
    </>
  );
}

function QualityManagerSection({ answers: a, set }: SectionProps) {
  return (
    <>
      <Choice
        name="has-quality-manager"
        legend="Does your organisation have a quality manager?"
        value={a.has_quality_manager}
        options={YES_NO}
        onChange={(v) => set({ has_quality_manager: v as "yes" | "no" })}
      />
      {a.has_quality_manager === "yes" && (
        <div className="grid gap-3 sm:grid-cols-3">
          <Text
            id="qm-name"
            label="Quality manager’s name"
            value={a.quality_manager_name}
            onChange={(v) => set({ quality_manager_name: v })}
          />
          <Text
            id="qm-phone"
            label="Telephone"
            type="tel"
            value={a.quality_manager_phone}
            onChange={(v) => set({ quality_manager_phone: v })}
          />
          <Text
            id="qm-email"
            label="Email"
            type="email"
            value={a.quality_manager_email}
            onChange={(v) => set({ quality_manager_email: v })}
          />
        </div>
      )}
    </>
  );
}

function IsoSection({ request, answers: a, set }: SectionProps) {
  const fuller = request.tier !== "low";
  return (
    <>
      <Choice
        name="iso-9001"
        legend="Is your organisation accredited to ISO 9001?"
        value={a.iso_9001}
        options={YES_NO}
        onChange={(v) => set({ iso_9001: v as "yes" | "no" })}
      />
      {a.iso_9001 === "yes" && (
        <>
          <Attach
            id="iso-certificate"
            answer="iso_9001_certificate"
            label="ISO 9001 certificate"
            hint="A PDF or a photo."
            value={a.iso_9001_certificate}
            onChange={(iso_9001_certificate) => set({ iso_9001_certificate })}
          />
          {fuller && (
            <p className="text-xs text-muted-foreground">
              With your ISO 9001 certificate attached, you skip the quality
              questions.
            </p>
          )}
        </>
      )}
      {a.iso_9001 === "no" && fuller && (
        <p className="text-xs text-muted-foreground">
          You’ll answer the quality questions instead.
        </p>
      )}
      <Text
        id="other-accreditations"
        label="Any other accreditations you hold (optional)"
        value={a.other_accreditations}
        placeholder="For example ISO 14001, with its expiry date"
        onChange={(v) => set({ other_accreditations: v })}
      />
      {request.fsc_needed && (
        <>
          <Banner tone="warn">
            FSC-labelled work will go through you, so we need your FSC or PEFC
            chain-of-custody certificate.
          </Banner>
          <Attach
            id="fsc-certificate"
            answer="fsc_certificate"
            label="FSC or PEFC certificate"
            hint="A PDF."
            value={a.fsc_certificate}
            onChange={(fsc_certificate) => set({ fsc_certificate })}
          />
        </>
      )}
    </>
  );
}

function QualityQuestionsSection({ request, answers: a, set }: SectionProps) {
  if (a.iso_9001 === "yes")
    return (
      <Banner tone="ok">
        Skipped: you’re accredited to ISO 9001 and have given us your
        certificate.
      </Banner>
    );
  return (
    <div className="grid divide-y">
      {request.quality_questions.map((n) => (
        <div key={n} className="py-3 first:pt-0">
          <Choice
            name={`quality-${n}`}
            legend={`${n}. ${QUALITY_QUESTIONS[n - 1]}`}
            value={a.quality_answers[n] ?? ""}
            options={YES_NO}
            onChange={(v) =>
              set({
                quality_answers: {
                  ...a.quality_answers,
                  [n]: v as "yes" | "no",
                },
              })
            }
          />
        </div>
      ))}
    </div>
  );
}

function InsuranceSection({ answers: a, set }: SectionProps) {
  return (
    <>
      <Attach
        id="public-liability"
        answer="public_liability"
        label="Public liability certificate"
        hint="A PDF or a photo."
        value={a.public_liability}
        onChange={(public_liability) => set({ public_liability })}
      />
      <Attach
        id="employers-liability"
        answer="employers_liability"
        label="Employer’s liability certificate"
        hint="A PDF or a photo."
        value={a.employers_liability}
        onChange={(employers_liability) => set({ employers_liability })}
      />
    </>
  );
}

function SlaverySection({ answers: a, set }: SectionProps) {
  return (
    <>
      <Choice
        name="slavery"
        legend="Is your annual turnover over £36m, the Modern Slavery Act 2015 threshold?"
        value={a.over_slavery_threshold}
        options={YES_NO}
        onChange={(v) => set({ over_slavery_threshold: v as "yes" | "no" })}
      />
      {a.over_slavery_threshold === "yes" && (
        <Attach
          id="slavery-statement"
          answer="slavery_statement"
          label="Your latest published statement"
          hint="A PDF."
          value={a.slavery_statement}
          onChange={(slavery_statement) => set({ slavery_statement })}
        />
      )}
    </>
  );
}

const BLANK_GDPR_ANSWER: GdprAnswer = { answer: "", comment: "" };

function GdprSection({ answers: a, set }: SectionProps) {
  const setQuestion = (id: string, change: Partial<GdprAnswer>) =>
    set({
      gdpr_answers: {
        ...a.gdpr_answers,
        [id]: { ...(a.gdpr_answers[id] ?? BLANK_GDPR_ANSWER), ...change },
      },
    });
  return (
    <>
      <Choice
        name="ico-complaint"
        legend="Has your organisation ever been the subject of a complaint that was referred to the ICO?"
        value={a.ico_complaint}
        options={YES_NO}
        onChange={(v) => set({ ico_complaint: v as "yes" | "no" })}
      />
      {a.ico_complaint === "yes" && (
        <FormField
          id="ico-complaint-outcome"
          label="Please summarise the outcome"
        >
          {(control) => (
            <Textarea
              {...control}
              className={INPUT_CLASS}
              rows={4}
              value={a.ico_complaint_outcome}
              onChange={(event) =>
                set({ ico_complaint_outcome: event.target.value })
              }
            />
          )}
        </FormField>
      )}
      <SubHeading>Data security measures</SubHeading>
      <p className="text-sm">
        Personal data we pass to you must be processed on systems that have
        technical and organisational measures protecting it. You assure us that
        you will take all the measures needed to process it on your systems, in
        line with what you tell us below.
      </p>
      <p className="text-xs text-muted-foreground">
        Every question needs a few words of explanation, and most also need a
        Yes or No. If a question doesn’t apply to you, say why.
      </p>
      <div className="grid divide-y">
        {GDPR_QUESTIONS.map((question, index) => {
          const given = a.gdpr_answers[question.id];
          return (
            <div key={question.id} className="grid gap-2 py-3 first:pt-0">
              {question.kind === "yes_no" ? (
                <Choice
                  name={`gdpr-${question.id}`}
                  legend={`${index + 1}. ${question.label}`}
                  value={given?.answer ?? ""}
                  options={YES_NO}
                  onChange={(v) =>
                    setQuestion(question.id, { answer: v as "yes" | "no" })
                  }
                />
              ) : (
                <p id={`gdpr-${question.id}-question`} className="text-sm">
                  {index + 1}. {question.label}
                </p>
              )}
              <FormField
                id={`gdpr-${question.id}-comment`}
                label={question.detail_label}
              >
                {(control) => (
                  <Textarea
                    {...control}
                    aria-describedby={
                      question.kind === "explanation"
                        ? `gdpr-${question.id}-question`
                        : control["aria-describedby"]
                    }
                    className={INPUT_CLASS}
                    rows={3}
                    value={given?.comment ?? ""}
                    onChange={(event) =>
                      setQuestion(question.id, { comment: event.target.value })
                    }
                  />
                )}
              </FormField>
            </div>
          );
        })}
      </div>
      <SubHeading>Data processing agreement</SubHeading>
      <Attach
        id="dpa"
        answer="dpa"
        label="Signed data processing agreement (DPA)"
        hint="We can’t send you personal data until it is in place."
        value={a.dpa}
        onChange={(dpa) => set({ dpa })}
      />
    </>
  );
}

function SecuritySection({ answers: a, set }: SectionProps) {
  return (
    <>
      <Choice
        name="security"
        legend="Do you hold Cyber Essentials, Cyber Essentials Plus or ISO 27001?"
        value={a.security_held}
        options={SECURITY_OPTIONS.map((o) => [o, o] as const)}
        onChange={(security_held) => set({ security_held })}
      />
      {a.security_held && a.security_held !== "None of these" && (
        <Attach
          id="security-certificate"
          answer="security_certificate"
          label={`${a.security_held} certificate`}
          hint="A PDF."
          value={a.security_certificate}
          onChange={(security_certificate) => set({ security_certificate })}
        />
      )}
    </>
  );
}

function ContinuitySection({ answers: a, set }: SectionProps) {
  return (
    <>
      <Choice
        name="continuity-plan"
        legend="Do you have a written business continuity plan?"
        value={a.continuity_plan}
        options={YES_NO}
        onChange={(v) => set({ continuity_plan: v as "yes" | "no" })}
      />
      <Choice
        name="resume"
        legend="If your main site were unavailable, how soon could you resume work for us?"
        value={a.resume_within}
        options={RESUME_OPTIONS.map((o) => [o, o] as const)}
        onChange={(resume_within) => set({ resume_within })}
      />
    </>
  );
}

function AgreementSection({ agreement, answers: a, set }: SectionProps) {
  return (
    <>
      <div
        role="region"
        tabIndex={0}
        aria-label="The confidentiality and non-solicitation agreement"
        className="grid max-h-80 gap-3 overflow-y-auto rounded-lg border border-gray-300 bg-card p-3.5 text-sm dark:border-input"
      >
        {agreement.text.split("\n\n").map((paragraph, i) => (
          <p key={i} className="whitespace-pre-line">
            {paragraph}
          </p>
        ))}
      </div>
      <label className="inline-flex cursor-pointer items-start gap-2.5 text-sm">
        <input
          type="checkbox"
          checked={a.agreed}
          onChange={(event) => set({ agreed: event.target.checked })}
          className="mt-0.5 size-4 accent-primary"
        />
        I accept the confidentiality and non-solicitation agreement on behalf of{" "}
        {a.company_name || "my company"}.
      </label>
    </>
  );
}
